import { useState } from 'react'
import { useToggle } from 'usehooks-ts'

import { formatNumber } from '@fv/client-core'
import { type DateRange, pastRangeNames, workflows } from '@fv/models'
import { DEFAULT_USAGE_STAT_RANGE, type UsageStatType } from '@fv/models/admin'

import { AdminButton } from '@/components/shared/AdminButton'
import { AdminDateRangeMenu } from '@/components/shared/AdminDateRangeMenu'
import { AdminSection } from '@/components/shared/AdminSection'
import { AdminTooltip } from '@/components/shared/AdminTooltip'

import { useUsageStat } from './api'
import type { UsageStatScope, UsageStatSettings } from './types'
import { UsageStatConfig } from './UsageStatConfig'

type Props = {
  stat: UsageStatType
  label: string
  scope?: UsageStatScope
  defaultRange?: DateRange
}

export const UsageStat = ({
  stat,
  label,
  scope,
  defaultRange = DEFAULT_USAGE_STAT_RANGE,
}: Props) => {
  const [configOpen, toggleConfigOpen] = useToggle()
  const [settings, setSettings] = useState<UsageStatSettings>({
    range: defaultRange,
    workflows: [...workflows],
  })
  const { range } = settings
  const isLoadStat = stat.startsWith('loads-')

  const query = useUsageStat({ stat, ...scope, ...settings })

  return (
    <AdminSection
      title={label}
      headerClassName="text-sm"
      headerActions={
        <div className="ml-auto flex items-center gap-1">
          <AdminDateRangeMenu
            ranges={pastRangeNames}
            allowCustom
            value={range}
            onChange={value => setSettings({ ...settings, range: value })}
          />
          {isLoadStat && (
            <>
              <AdminButton
                icon="cog"
                className="btn-xs"
                noOutline
                onClick={toggleConfigOpen}
              />
              <UsageStatConfig
                isOpen={configOpen}
                onClose={toggleConfigOpen}
                label={label}
                settings={settings}
                onChange={setSettings}
              />
            </>
          )}
        </div>
      }
    >
      <div className="text-3xl font-bold text-center py-3">
        {query.isLoading ? (
          <span className="loading loading-dots loading-sm" />
        ) : query.isError ? (
          <AdminTooltip label="Error fetching usage data">
            <span>?</span>
          </AdminTooltip>
        ) : (
          formatNumber(query.data?.value ?? 0)
        )}
      </div>
    </AdminSection>
  )
}
